"use client";
import React, { useState, useCallback } from "react";
import { 
  Box, Card, CardContent, Typography, Stack, IconButton, 
  Divider, Container, Accordion, AccordionSummary, AccordionDetails,
  Avatar, alpha, useTheme, Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";

import ContactTable from "./ContactTable";
import GPContactTable from "./GPContactTable";
import ProfessionalContactTable from "./ProfessionalContactTable";
import ContactModal from "./ContactModal";
import GPModal from "./GPModal";
import ProfessionalModal from "./ProfessionalModal";
import EditProfessionalContactModal from "./editProfessionalContactModal";
import ToggleSwitch from "./ToggleSwitch";
import CommonButton from "../../CustomForm/CommonButton";

const ContactAndFamily = () => {
  const theme = useTheme();
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [showGPModal, setShowGPModal] = useState(false);
  const [showProfessionalModal, setShowProfessionalModal] = useState(false);
  const [showEditProfessionalModal, setShowEditProfessionalModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  const [expanded, setExpanded] = useState('panel1');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleEditProfessional = useCallback((data) => {
    setSelectedProfessional(data);
    setShowEditProfessionalModal(true);
  }, []);

  const handleEditClose = () => {
    setShowEditProfessionalModal(false);
    setSelectedProfessional(null);
  };

  const SectionHeader = ({ title, icon: Icon, onAdd, iconColor = "#2469F0" }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", pr: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(iconColor, 0.08),
            color: iconColor,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1rem" }}>
          {title}
        </Typography>
        <Chip
          label="New"
          size="small"
          sx={{
            height: 16,
            fontSize: "0.5rem",
            fontWeight: 600,
            bgcolor: "#D1FAE5",
            color: "#059669",
          }}
        />
      </Stack>
      <IconButton 
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }} 
        size="small"
        sx={{ 
          color: "#2469F0",
          "&:hover": { 
            bgcolor: alpha("#2469F0", 0.04),
          }
        }}
      >
        <AddCircleIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </Stack>
  );

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          }}
        >
          <ContactPhoneIcon sx={{ fontSize: 22 }} />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A" }}>
            Contact & Family
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage contact details and permissions
          </Typography>
        </Box>
      </Box> */}
        
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: "12px", 
          boxShadow: "0px 2px 8px rgba(0,0,0,0.02)", 
          border: "1px solid #E2E8F0",
          overflow: "hidden"
        }}
      >
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#0F172A" }}>
                Consent to share care details
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                Consent has been obtained to share care details with family members.
              </Typography>
            </Box>
            <ToggleSwitch />
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleAccordionChange('panel1')} 
          sx={{ 
            borderRadius: "12px !important", 
            border: "1px solid #E2E8F0",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.02)",
            "&:before": { display: "none" },
            overflow: "hidden"
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: "#64748B" }} />}
            sx={{
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
              }
            }}
          >
            <SectionHeader 
              title="Personal Contacts" 
              icon={FamilyRestroomIcon} 
              onAdd={() => setShowPersonalModal(true)} 
              iconColor="#2469F0"
            />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Divider sx={{ mb: 0 }} />
            <ContactTable />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleAccordionChange('panel2')} 
          sx={{ 
            borderRadius: "12px !important", 
            border: "1px solid #E2E8F0",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.02)",
            "&:before": { display: "none" },
            overflow: "hidden"
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: "#64748B" }} />}
            sx={{
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
              }
            }}
          >
            <SectionHeader 
              title="General Practitioner (GP) Details" 
              icon={LocalHospitalIcon} 
              onAdd={() => setShowGPModal(true)} 
              iconColor="#EF4444"
            />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Divider sx={{ mb: 0 }} />
            <GPContactTable />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleAccordionChange('panel3')} 
          sx={{ 
            borderRadius: "12px !important", 
            border: "1px solid #E2E8F0",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.02)",
            "&:before": { display: "none" },
            overflow: "hidden"
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: "#64748B" }} />}
            sx={{
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
              }
            }}
          >
            <SectionHeader 
              title="Professional Contacts" 
              icon={BadgeIcon} 
              onAdd={() => setShowProfessionalModal(true)} 
              iconColor="#8B5CF6"
            />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Divider sx={{ mb: 0 }} />
            <ProfessionalContactTable onEdit={handleEditProfessional} />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <CommonButton />
      </Box>

      <ContactModal show={showPersonalModal} onHide={() => setShowPersonalModal(false)} />
      <GPModal show={showGPModal} onHide={() => setShowGPModal(false)} />
      <ProfessionalModal show={showProfessionalModal} onHide={() => setShowProfessionalModal(false)} />
      
      {showEditProfessionalModal && selectedProfessional && (
        <EditProfessionalContactModal
          show={showEditProfessionalModal}
          onHide={handleEditClose}
          data={selectedProfessional}
        />
      )}
    </Box>
  );
};

export default ContactAndFamily;